/**
 * The `core-api` pilot transport switch for list reads, resolved once per page
 * load: the build must carry `VITE_PILOT_API_URL` and `VITE_PILOT_DEPLOYMENT=dev`,
 * and the browser must have opted in before the page loaded. Opting in or out takes a reload, so one
 * infinite query never mixes transports across its pages.
 */

import { DEV_GCP_PROJECT_NUMBER } from './pilot-api.constants';

const OPT_IN_KEY = 'pp.pilot.coreApi';
// The deployed dev site builds in Vite mode `production`, so `MODE`/`PROD`
// cannot tell it from staging or production; only an explicit marker can.
const PILOT_DEPLOYMENT = 'dev';
const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1']);
// The browser sends the user's access token to this origin, so it must be the
// dev core-api service's deterministic Cloud Run URL: the committed dev project
// number and the dev cell's region (infra/cells/au-develop.yaml gcpRegion).
const CORE_API_HOST =
  DEV_GCP_PROJECT_NUMBER === null
    ? null
    : `core-api-${DEV_GCP_PROJECT_NUMBER}.australia-southeast1.run.app`;

/**
 * The value's origin when it is exactly the dev core-api Cloud Run origin (or
 * http on loopback for local development), else null.
 */
function parseOrigin(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const secure =
    (url.protocol === 'https:' &&
      CORE_API_HOST !== null &&
      url.hostname === CORE_API_HOST &&
      url.port === '') ||
    (url.protocol === 'http:' && LOOPBACK_HOSTS.has(url.hostname));
  // Anything beyond the origin (path, query, credentials) is a misconfiguration.
  return secure && url.origin === value.replace(/\/$/, '') ? url.origin : null;
}

// Storage can be missing or throw (privacy mode, blocked site data): that is off.
function hasOptedIn(): boolean {
  try {
    return globalThis.localStorage.getItem(OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
}

const buildEnv = import.meta.env as Record<string, string | undefined>;

/**
 * The pilot origin this build carries, whether or not this browser opted in;
 * null on any build not marked as the dev deployment.
 */
export const pilotApiOrigin: string | null =
  buildEnv.VITE_PILOT_DEPLOYMENT === PILOT_DEPLOYMENT
    ? parseOrigin(buildEnv.VITE_PILOT_API_URL)
    : null;

/** The origin list reads go to, or null when they stay on PostgREST. */
export const pilotTransportOrigin: string | null =
  pilotApiOrigin !== null && hasOptedIn() ? pilotApiOrigin : null;

/** True when list reads go to `core-api` for this page load. */
export const isPilotTransportOn: boolean = pilotTransportOrigin !== null;
